import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Upload, RotateCcw, Download, Save, History, Search, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg, deviceFingerprint } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const BASE_ROLES = ["super_admin", "doctor", "nurse", "receptionist"];

const FACILITY_FIELDS = [
  ["name", "Facility name"], ["short_name", "Short name"], ["address", "Address"],
  ["landmark", "Landmark"], ["city", "City"], ["state", "State"], ["country", "Country"],
  ["pincode", "PIN code"], ["phone", "Phone"], ["emergency_phone", "Emergency number"],
  ["whatsapp", "WhatsApp"], ["email", "Email"], ["website", "Website"], ["maps_link", "Google Maps link"],
  ["reg_number", "Registration number"], ["gst_number", "GST number"], ["pan_number", "PAN number"],
  ["nabh_number", "NABH number"], ["timings", "Timings"],
];

const STAFF_FIELDS = [
  ["full_name", "Full name"], ["qualification", "Qualification"], ["reg_number", "Registration number"],
  ["specialization", "Specialization"], ["department", "Department"], ["experience", "Experience"],
  ["email", "Email"], ["phone", "Phone"], ["working_days", "Working days"], ["working_hours", "Working hours"],
  ["consultation_fee", "Consultation fee", "number"], ["followup_fee", "Follow-up fee", "number"],
  ["emergency_fee", "Emergency fee", "number"], ["tele_fee", "Teleconsultation fee", "number"],
  ["appointment_duration", "Slot duration (min)", "number"], ["salary", "Salary", "number"],
  ["joining_date", "Joining date", "date"], ["password", "Reset password", "password"],
];

const CATEGORY_LABELS = {
  general: "General", branding: "Branding & theme", prescription: "Prescription document",
  pdf: "PDF layout", whatsapp: "WhatsApp", security: "Security", appointment: "Appointments",
  billing: "Billing", notifications: "Notifications", backup: "Backup",
};

/** Renders one setting control based on the value type. */
const SettingField = ({ item, value, onChange }) => {
  const t = item.type;
  if (t === "bool") {
    return (
      <div className="flex items-center justify-between gap-4 border-b border-border/60 py-2">
        <div>
          <p className="text-sm font-medium">{item.label}</p>
          <p className="text-[11px] font-mono text-muted-foreground">{item.key}</p>
        </div>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`setting-${item.key}`} />
      </div>
    );
  }
  if (t === "list") {
    return (
      <div className="space-y-1.5 border-b border-border/60 py-2">
        <Label className="text-xs">{item.label}</Label>
        <Textarea rows={2} value={(value || []).join(", ")} data-testid={`setting-${item.key}`}
          onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />
        <p className="text-[11px] font-mono text-muted-foreground">{item.key} · comma separated</p>
      </div>
    );
  }
  const long = typeof value === "string" && value.length > 70;
  return (
    <div className="space-y-1.5 border-b border-border/60 py-2">
      <Label className="text-xs">{item.label}</Label>
      {long ? (
        <Textarea rows={3} value={value ?? ""} data-testid={`setting-${item.key}`}
          onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input type={t === "int" || t === "float" ? "number" : "text"} value={value ?? ""}
          data-testid={`setting-${item.key}`}
          onChange={(e) => onChange(t === "int" || t === "float" ? Number(e.target.value) : e.target.value)} />
      )}
      <p className="text-[11px] font-mono text-muted-foreground">{item.key}</p>
    </div>
  );
};

export default function Settings() {
  const { facilities, refreshFacilities } = useApp();
  const [tab, setTab] = useState("config");
  const [category, setCategory] = useState("general");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [backups, setBackups] = useState([]);
  const [encBackups, setEncBackups] = useState([]);
  const [passphrase, setPassphrase] = useState("");
  const [roles, setRoles] = useState([]);
  const [permissionList, setPermissionList] = useState([]);

  const [facilityForm, setFacilityForm] = useState(null);
  const [staffForm, setStaffForm] = useState(null);
  const [roleForm, setRoleForm] = useState(null);
  const importRef = useRef(null);
  const assetRef = useRef(null);
  const assetTarget = useRef(null);

  const loadSettings = async () => {
    const params = [category ? `category=${category}` : "", search ? `q=${encodeURIComponent(search)}` : ""]
      .filter(Boolean).join("&");
    const { data } = await api.get(`/settings${params ? `?${params}` : ""}`);
    setCategories(data.categories);
    setItems(data.items);
    setDraft({});
  };

  const loadRest = () => {
    api.get("/users").then(({ data }) => setUsers(data));
    api.get("/devices").then(({ data }) => setDevices(data)).catch(() => {});
    api.get("/backups").then(({ data }) => setBackups(data)).catch(() => {});
    api.get("/backups/encrypted").then(({ data }) => setEncBackups(data)).catch(() => {});
    api.get("/roles").then(({ data }) => { setRoles(data.roles); setPermissionList(data.permissions); });
  };

  useEffect(() => { loadSettings(); /* eslint-disable-next-line */ }, [category, search]);
  useEffect(() => { loadRest(); }, []);

  const value = (item) => (item.key in draft ? draft[item.key] : item.value);
  const dirty = Object.keys(draft).length;

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/settings", { values: draft });
      toast.success(`${data.updated.length} setting(s) saved`);
      loadSettings();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resetCategory = async () => {
    try {
      const { data } = await api.post(`/settings/reset?category=${category}`);
      toast.success(`${data.reset} setting(s) reset to default`);
      loadSettings();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportSettings = async () => {
    const res = await api.get("/settings/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "medicore-settings.json"; a.click();
    toast.success("Settings exported");
  };

  const importSettings = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const { data } = await api.post("/settings/import", parsed);
      toast.success(`${data.imported.length} setting(s) imported`);
      loadSettings();
    } catch (err) { toast.error(errMsg(err) || "Invalid settings file"); }
    e.target.value = "";
  };

  const openHistory = async (key) => {
    const { data } = await api.get(`/settings/history${key ? `?key=${key}` : ""}`);
    setHistory(data);
    setHistoryOpen(true);
  };

  const restoreVersion = async (id) => {
    try {
      await api.post(`/settings/history/${id}/restore`);
      toast.success("Previous value restored");
      setHistoryOpen(false); loadSettings();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadAsset = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !assetTarget.current) return;
    const { kind, apply } = assetTarget.current;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post("/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data.file);
      toast.success("Image stored locally");
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  const pickAsset = (kind, apply) => {
    assetTarget.current = { kind, apply };
    setTimeout(() => assetRef.current?.click(), 30);
  };

  const saveFacility = async () => {
    try {
      if (facilityForm.id) await api.put(`/facilities/${facilityForm.id}`, facilityForm);
      else await api.post("/facilities", facilityForm);
      toast.success("Facility saved");
      setFacilityForm(null); refreshFacilities();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleFacilityStatus = async (id) => {
    try {
        await api.put(`/facilities/${id}/status`);
        toast.success("Facility status updated");
        refreshFacilities();
    } catch (e) {
        toast.error(errMsg(e));
    }
};
  const deleteFacility = async (id) => {
    try {
      const { data } = await api.delete(`/facilities/${id}`);
      toast.success(data.reason || "Facility deleted");
      refreshFacilities();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveStaff = async () => {
    try {
      const payload = { ...staffForm };
      if (!payload.password) delete payload.password;
      if (staffForm.id) await api.put(`/users/${staffForm.id}`, payload);
      else await api.post("/users", payload);
      toast.success("Staff record saved");
      setStaffForm(null); loadRest();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveRole = async () => {
    try {
      if (roleForm.id) await api.put(`/roles/${roleForm.id}`, roleForm);
      else await api.post("/roles", roleForm);
      toast.success("Role saved");
      setRoleForm(null); loadRest();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runBackup = async () => {
    try { const { data } = await api.post("/backup"); toast.success(`Backup created: ${data.file}`); loadRest(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const runEncryptedBackup = async () => {
    if (passphrase.length < 8) return toast.error("Passphrase must be at least 8 characters");
    try {
      const { data } = await api.post("/backup/encrypted", { passphrase });
      toast.success(`AES-256 backup created: ${data.file}`);
      setPassphrase(""); loadRest();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const TABS = [["config", "Configuration"], ["facilities", "Hospitals & clinics"],
    ["staff", "Doctors & staff"], ["roles", "Roles & permissions"], ["system", "Devices & backup"]];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <input type="file" ref={importRef} className="hidden" accept=".json"
        onChange={importSettings} data-testid="import-settings-input" />
      <input type="file" ref={assetRef} className="hidden" accept=".png,.jpg,.jpeg,.webp,.svg,.ico"
        onChange={uploadAsset} data-testid="asset-file-input" />

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">Administration</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {TABS.map(([k, label]) => (
            <Button key={k} size="sm" variant={tab === k ? "default" : "outline"}
              data-testid={`settings-tab-${k}`} onClick={() => setTab(k)}>{label}</Button>
          ))}
        </div>
      </div>

      {tab === "config" && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input placeholder="Search settings" value={search} className="pl-8 w-64"
                data-testid="settings-search-input" onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-56" data-testid="settings-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={saveSettings} disabled={!dirty} className="gap-1.5"
              data-testid="settings-save-button"><Save className="h-3.5 w-3.5" /> Save{dirty ? ` (${dirty})` : ""}</Button>
            <Button size="sm" variant="outline" onClick={() => setDraft({})} disabled={!dirty}
              className="gap-1.5" data-testid="settings-cancel-button"><Undo2 className="h-3.5 w-3.5" /> Undo changes</Button>
            <Button size="sm" variant="outline" onClick={resetCategory} className="gap-1.5"
              data-testid="settings-reset-button"><RotateCcw className="h-3.5 w-3.5" /> Reset to default</Button>
            <Button size="sm" variant="outline" onClick={exportSettings} className="gap-1.5"
              data-testid="settings-export-button"><Download className="h-3.5 w-3.5" /> Export</Button>
            <Button size="sm" variant="outline" onClick={() => importRef.current?.click()} className="gap-1.5"
              data-testid="settings-import-button"><Upload className="h-3.5 w-3.5" /> Import</Button>
            <Button size="sm" variant="outline" onClick={() => openHistory()} className="gap-1.5"
              data-testid="settings-history-button"><History className="h-3.5 w-3.5" /> Version history</Button>
          </div>

          <Card className="shadow-sm">
            <CardHeader className="pb-1">
              <CardTitle className="text-sm">{CATEGORY_LABELS[category] || category}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-x-8 md:grid-cols-2">
              {items.map((item) => (
                <div key={item.key} className="flex items-start gap-2">
                  <div className="flex-1">
                    <SettingField item={item} value={value(item)}
                      onChange={(v) => setDraft({ ...draft, [item.key]: v })} />
                  </div>
                  <div className="flex flex-col gap-1 pt-6">
                    {item.overridden && <Badge variant="outline" className="text-[9px]">edited</Badge>}
                    {["branding.hospital_logo", "branding.watermark_image"].includes(item.key) && (
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        data-testid={`upload-${item.key}`}
                        onClick={() => pickAsset("hospital_logo", (f) => setDraft((d) => ({ ...d, [item.key]: f })))}>
                        <Upload className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" title="History"
                      data-testid={`history-${item.key}`} onClick={() => openHistory(item.key)}>
                      <History className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-muted-foreground py-6">No settings match.</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "facilities" && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Hospitals, clinics & branches</CardTitle>
            <Button size="sm" className="gap-1.5" data-testid="add-facility-button"
              onClick={() => setFacilityForm({ name: "", type: "clinic", country: "India", is_active: true })}>
              <Plus className="h-3.5 w-3.5" /> Add facility
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="facilities-table">
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>City</TableHead>
                <TableHead>Phone</TableHead><TableHead>Reg. no.</TableHead><TableHead>Logo</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {facilities.map((f) => (
                  <TableRow key={f.id} data-testid={`facility-row-${f.id}`}>
                    <TableCell className="font-medium">{f.name}
                      <span className="block text-xs text-muted-foreground">{f.address}</span>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{f.type}</Badge></TableCell>
                    <TableCell className="text-xs">{f.city || "—"}</TableCell>
                    <TableCell className="text-xs font-mono">{f.phone || "—"}</TableCell>
                    <TableCell className="text-xs font-mono">{f.reg_number || "—"}</TableCell>
                    <TableCell>
                      {f.logo ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/${f.logo}`}
                          alt="logo" className="h-7 object-contain" />
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge
                          className={`cursor-pointer text-white ${
                             f.is_active
                                 ? "bg-green-600 hover:bg-green-700"
                                 : "bg-red-600 hover:bg-red-700"
                          }`}
                          onClick={() => toggleFacilityStatus(f.id)}
                        >
                          {f.is_active ? "Active" : "Inactive"}
                        </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" data-testid={`edit-facility-${f.id}`}
                        onClick={() => setFacilityForm({ ...f })}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" data-testid={`delete-facility-${f.id}`}
                        onClick={() => deleteFacility(f.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "staff" && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Doctors & staff</CardTitle>
            <Button size="sm" className="gap-1.5" data-testid="add-user-button"
              onClick={() => setStaffForm({ username: "", password: "", full_name: "", role: "doctor" })}>
              <Plus className="h-3.5 w-3.5" /> Add staff
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="users-table">
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Username</TableHead><TableHead>Role</TableHead>
                <TableHead>Specialization</TableHead><TableHead>Fees</TableHead><TableHead>Signature</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.full_name}
                      <span className="block text-xs text-muted-foreground">{u.qualification}</span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{u.username}</TableCell>
                    <TableCell><Badge variant="outline">{(u.role_name || u.role).replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-xs">{u.specialization || "—"}</TableCell>
                    <TableCell className="text-xs tabular-nums">₹{u.consultation_fee || 0}</TableCell>
                    <TableCell>
                      {u.signature ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/${u.signature}`}
                          alt="sign" className="h-6 object-contain" />
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" data-testid={`edit-user-${u.id}`}
                        onClick={() => setStaffForm({ ...u, password: "" })}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" data-testid={`delete-user-${u.id}`}
                        onClick={async () => {
                          try { await api.delete(`/users/${u.id}`); toast.success("User removed"); loadRest(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "roles" && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Custom roles & permission toggles</CardTitle>
            <Button size="sm" className="gap-1.5" data-testid="add-role-button"
              onClick={() => setRoleForm({ name: "", label: "", base_role: "receptionist", permissions: {} })}>
              <Plus className="h-3.5 w-3.5" /> Create role
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="roles-table">
              <TableHeader><TableRow>
                <TableHead>Role</TableHead><TableHead>Base role</TableHead>
                <TableHead>Enabled permissions</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {roles.map((r) => (
                  <TableRow key={r.id} data-testid={`role-row-${r.id}`}>
                    <TableCell className="font-medium">{r.label || r.name}
                      <span className="block text-xs font-mono text-muted-foreground">{r.name}</span>
                    </TableCell>
                    <TableCell><Badge variant="outline">{r.base_role.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-xs">
                      {r.is_system ? "System role (base permissions)"
                        : Object.entries(r.permissions || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "none"}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {!r.is_system && (
                        <>
                          <Button size="icon" variant="ghost" data-testid={`edit-role-${r.id}`}
                            onClick={() => setRoleForm({ ...r })}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" data-testid={`delete-role-${r.id}`}
                            onClick={async () => {
                              try { await api.delete(`/roles/${r.id}`); toast.success("Role deleted"); loadRest(); }
                              catch (e) { toast.error(errMsg(e)); }
                            }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "system" && (
        <div className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Authorized devices</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table data-testid="devices-table">
                <TableHeader><TableRow>
                  <TableHead>Fingerprint</TableHead><TableHead>Device</TableHead>
                  <TableHead>Last seen</TableHead><TableHead className="text-right">Authorized</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {devices.map((d) => (
                    <TableRow key={d.id} data-testid={`device-row-${d.id}`}>
                      <TableCell className="font-mono text-xs">{d.fingerprint}
                        {d.fingerprint === deviceFingerprint() && (
                          <Badge variant="secondary" className="ml-2 text-[10px]">this device</Badge>
                        )}
                      </TableCell>
                      <TableCell>{d.name}</TableCell>
                      <TableCell className="text-xs">{new Date(d.last_seen).toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <Switch checked={d.authorized} data-testid={`device-switch-${d.id}`}
                          onCheckedChange={async (v) => {
                            try { await api.post(`/devices/${d.id}/authorize?authorized=${v}`); loadRest(); }
                            catch (e) { toast.error(errMsg(e)); }
                          }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-sm">Backups</CardTitle>
              <Button size="sm" onClick={runBackup} data-testid="run-backup-button">One click backup</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2 items-end max-w-lg">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs">AES-256 backup passphrase (min 8 characters)</Label>
                  <Input type="password" value={passphrase} data-testid="backup-passphrase-input"
                    onChange={(e) => setPassphrase(e.target.value)} />
                </div>
                <Button onClick={runEncryptedBackup} data-testid="encrypted-backup-button">Encrypt & save</Button>
              </div>
              <Table data-testid="backups-table">
                <TableHeader><TableRow>
                  <TableHead>File</TableHead><TableHead>Type</TableHead><TableHead>Size</TableHead><TableHead>Created</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {[...encBackups.map((b) => ({ ...b, enc: true })), ...backups].map((b) => (
                    <TableRow key={b.file}>
                      <TableCell className="font-mono text-xs">{b.file}</TableCell>
                      <TableCell>
                        <Badge variant={b.enc ? "secondary" : "outline"}>{b.enc ? "AES-256" : "Plain"}</Badge>
                      </TableCell>
                      <TableCell>{b.size_kb} KB</TableCell>
                      <TableCell className="text-xs">{new Date(b.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ---------------------------------------------------------------- dialogs */}
      <Dialog open={!!facilityForm} onOpenChange={(o) => !o && setFacilityForm(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="facility-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {facilityForm?.id ? `Edit ${facilityForm.name}` : "Add hospital / clinic"}
            </DialogTitle>
          </DialogHeader>
          {facilityForm && (
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select value={facilityForm.type} onValueChange={(v) => setFacilityForm({ ...facilityForm, type: v })}>
                  <SelectTrigger data-testid="facility-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["hospital", "clinic", "branch"].map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {FACILITY_FIELDS.map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <Input value={facilityForm[k] ?? ""} data-testid={`facility-${k}-input`}
                    onChange={(e) => setFacilityForm({ ...facilityForm, [k]: e.target.value })} />
                </div>
              ))}
              {[["logo", "Logo"], ["letterhead_logo", "Letterhead logo"], ["seal", "Hospital seal"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <div className="flex gap-2 items-center">
                    <Input value={facilityForm[k] ?? ""} readOnly className="text-xs" />
                    <Button size="icon" variant="outline" data-testid={`facility-upload-${k}`}
                      onClick={() => pickAsset(k === "seal" ? "seal" : "hospital_logo",
                        (file) => setFacilityForm((f) => ({ ...f, [k]: file })))}>
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFacilityForm(null)}>Cancel</Button>
            <Button onClick={saveFacility} data-testid="save-facility-button">Save facility</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!staffForm} onOpenChange={(o) => !o && setStaffForm(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {staffForm?.id ? `Edit ${staffForm.full_name}` : "Create staff account"}
            </DialogTitle>
          </DialogHeader>
          {staffForm && (
            <div className="grid sm:grid-cols-3 gap-4">
              {!staffForm.id && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Username</Label>
                    <Input value={staffForm.username ?? ""} data-testid="user-username-input"
                      onChange={(e) => setStaffForm({ ...staffForm, username: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Password</Label>
                    <Input type="password" value={staffForm.password ?? ""} data-testid="user-password-input"
                      onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} />
                  </div>
                </>
              )}
              {STAFF_FIELDS.map(([k, label, kind]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <Input type={kind || "text"} value={staffForm[k] ?? ""} data-testid={`user-${k}-input`}
                    onChange={(e) => setStaffForm({
                      ...staffForm,
                      [k]: kind === "number" ? Number(e.target.value) : e.target.value,
                    })} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-xs">Base role</Label>
                <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BASE_ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Custom role (optional)</Label>
                <Select value={staffForm.role_name || "none"}
                  onValueChange={(v) => setStaffForm({ ...staffForm, role_name: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-custom-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {roles.filter((r) => !r.is_system).map((r) => (
                      <SelectItem key={r.id} value={r.name}>{r.label || r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Facility</Label>
                <Select value={String(staffForm.facility_id || "")}
                  onValueChange={(v) => setStaffForm({ ...staffForm, facility_id: Number(v) })}>
                  <SelectTrigger data-testid="user-facility-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {facilities.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {[["signature", "Signature image"], ["stamp", "Doctor stamp"], ["photo", "Photograph"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <div className="flex gap-2 items-center">
                    <Input value={staffForm[k] ?? ""} readOnly className="text-xs" />
                    <Button size="icon" variant="outline" data-testid={`user-upload-${k}`}
                      onClick={() => pickAsset(k === "photo" ? "photo" : k,
                        (file) => setStaffForm((s) => ({ ...s, [k]: file })))}>
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffForm(null)}>Cancel</Button>
            <Button onClick={saveStaff} data-testid="save-user-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!roleForm} onOpenChange={(o) => !o && setRoleForm(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader><DialogTitle className="font-heading">
            {roleForm?.id ? `Edit role ${roleForm.name}` : "Create custom role"}
          </DialogTitle></DialogHeader>
          {roleForm && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Role key</Label>
                  <Input value={roleForm.name} disabled={!!roleForm.id} data-testid="role-name-input"
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Display label</Label>
                  <Input value={roleForm.label} data-testid="role-label-input"
                    onChange={(e) => setRoleForm({ ...roleForm, label: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Base role (security floor)</Label>
                  <Select value={roleForm.base_role} onValueChange={(v) => setRoleForm({ ...roleForm, base_role: v })}>
                    <SelectTrigger data-testid="role-base-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BASE_ROLES.map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                {permissionList.map((p) => (
                  <div key={p} className="flex items-center justify-between border border-border rounded-md px-3 py-1.5">
                    <span className="text-xs capitalize">{p.replace(/_/g, " ")}</span>
                    <Switch checked={!!roleForm.permissions?.[p]} data-testid={`permission-${p}`}
                      onCheckedChange={(v) => setRoleForm({
                        ...roleForm, permissions: { ...roleForm.permissions, [p]: v },
                      })} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleForm(null)}>Cancel</Button>
            <Button onClick={saveRole} data-testid="save-role-button">Save role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="settings-history-dialog">
          <DialogHeader><DialogTitle className="font-heading">Settings version history</DialogTitle></DialogHeader>
          <Table data-testid="settings-history-table">
            <TableHeader><TableRow>
              <TableHead>When</TableHead><TableHead>Key</TableHead><TableHead>Old</TableHead>
              <TableHead>New</TableHead><TableHead>By</TableHead><TableHead className="text-right">Restore</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">
                  No changes recorded yet.
                </TableCell></TableRow>
              )}
              {history.map((v) => (
                <TableRow key={v.id} data-testid={`history-row-${v.id}`}>
                  <TableCell className="text-xs font-mono">{new Date(v.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs font-mono">{v.key}</TableCell>
                  <TableCell className="text-xs max-w-[140px] truncate">{JSON.stringify(v.old_value)}</TableCell>
                  <TableCell className="text-xs max-w-[140px] truncate">{JSON.stringify(v.new_value)}</TableCell>
                  <TableCell className="text-xs">{v.changed_by}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`restore-version-${v.id}`}
                      onClick={() => restoreVersion(v.id)}>Restore</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
